<template>
	<div class="entry-details-lightbox__header flex gap-1 justify-start items-start min-w-0 w-full">
		<div v-if="isPersonEntry" class="entry-details-lightbox__header-person flex items-center gap-3 min-w-0">
			<CAvatar
				class="entry-details-lightbox__header-avatar shrink-0"
				:avatar="avatarValue"
				:icon="avatarIcon"
				size="m"
			/>
			<span class="entry-details-lightbox__header-person-name truncate font-display text-4">{{ summary }}</span>
		</div>
		<div v-else class="entry-details-lightbox__header-submission flex min-w-0 flex-shrink flex-col gap-1">
			<div class="entry-details-lightbox__header-summary-row flex items-center min-w-0 gap-2">
				<span class="entry-details-lightbox__header-summary truncate font-display text-4">{{ summary }}</span>
			</div>
			<div v-if="breadcrumbItems.length" class="entry-details-lightbox__header-breadcrumb flex items-center min-w-0 gap-2 text-subtle text-2">
				<span v-for="item in breadcrumbItems" :key="item.key" class="entry-details-lightbox__header-breadcrumb-item inline-flex items-center min-w-0 gap-1">
					<Inline :svg="item.icon" class="entry-details-lightbox__header-breadcrumb-icon shrink-0 text-icon-subtle" />
					<span class="entry-details-lightbox__header-breadcrumb-label truncate">{{ item.label }}</span>
				</span>
			</div>
		</div>
		<CToolTip placement="top" :use-button="false">
			<template #button>
				<CIconButton
					class="entry-details-lightbox__entry-link -mt-3 shrink-0"
					icon="chevron-right"
					:intense="false"
					:aria-label="entryLinkAriaLabel"
					:disabled="!entryDetailsRoute"
					@click="handleNavigate"
				/>
			</template>
			<span>{{ entryLinkTooltip }}</span>
		</CToolTip>
		<div class="entry-details-lightbox__header-actions flex items-center gap-2 ml-auto">
			<CLinkedPersonAvatar
				:entry-id="entryId"
				:person-submissions-enabled="personSubmissionsEnabled"
				:is-person-entry="isPersonEntry"
			/>
		</div>
	</div>
</template>

<script lang="ts">
	import { computed, defineComponent, toRef, type PropType } from 'vue';
	import CAvatar from 'src/components/auth/Avatar.vue';
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import Inline from 'src/components/content/Inline.vue';
	import { getAvatar } from '@cognitoforms/utils/avatar';
	import type { UserFormMeta } from '@cognitoforms/types/server-types/core/services/user-form-meta';
	import type { CognitoCurrentView } from './entry-details/entry-details-lightbox.types';
	import { useEntryDetailsNavigation } from './entry-details/use-entry-details-navigation';
	import { usePersonAvatarUrl } from './entry-details/use-person-avatar-url';
	import CLinkedPersonAvatar from './LinkedPersonAvatar.vue';

	type SplitNameResult = {
		firstName: string | null;
		lastName: string | null;
	};

	type BreadcrumbItem = {
		key: 'folder' | 'form';
		icon: 'folder' | 'form';
		label: string;
	};

	type HeaderFormMeta = Pick<UserFormMeta, 'Name' | 'Title' | 'UrlName' | 'FolderId' | 'EntryViews'>;

	export default defineComponent({
		name: 'CEntryDetailsLightboxTitle',
		components: {
			CAvatar,
			CIconButton,
			CToolTip,
			Inline,
			CLinkedPersonAvatar
		},
		props: {
			isPersonEntry: { type: Boolean, required: true },
			personSubmissionsEnabled: { type: Boolean, default: false },
			summary: { type: String, default: '' },
			personEmail: { type: String, default: '' },
			entryId: { type: String, required: true },
			internalEntryId: { type: String, default: '' },
			viewId: { type: String, default: '' },
			currentView: { type: Object as PropType<CognitoCurrentView | null>, default: null },
			formMeta: { type: Object as PropType<HeaderFormMeta | null>, default: null },
			folderName: { type: String, default: '' },
			organizationCode: { type: String, default: '' }
		},
		emits: ['navigate'],
		setup(props, { emit }) {
			const whitespaceRegex = /\s+/;

			const splitName = (fullName: string): SplitNameResult => {
				const parts = fullName.split(whitespaceRegex).filter(Boolean);
				if (parts.length === 0)
					return { firstName: null, lastName: null };

				if (parts.length === 1)
					return { firstName: parts[0], lastName: null };

				return {
					firstName: parts[0],
					lastName: parts[parts.length - 1]
				};
			};

			const breadcrumbItems = computed(() => {
				const items: BreadcrumbItem[] = [];

				const folderName = props.folderName.trim();
				if (folderName) {
					items.push({
						key: 'folder',
						icon: 'folder',
						label: folderName
					});
				}

				const formTitle = (props.formMeta?.Title || '').trim();
				const formName = formTitle || (props.formMeta?.Name || '').trim();
				if (formName) {
					items.push({
						key: 'form',
						icon: 'form',
						label: formName
					});
				}

				return items;
			});

			const uploadedAvatarUrl = usePersonAvatarUrl(toRef(props, 'entryId'), toRef(props, 'isPersonEntry'));

			const personHeaderAvatar = computed(() => {
				const fallbackName = props.summary.trim();
				const { firstName, lastName } = splitName(fallbackName);

				if (!firstName && !uploadedAvatarUrl.value)
					return '';

				return getAvatar({
					FirstName: firstName,
					LastName: lastName,
					Email: props.personEmail || undefined,
					AvatarUrl: uploadedAvatarUrl.value || undefined
				});
			});

			const avatarValue = computed(() => personHeaderAvatar.value || undefined);
			const avatarIcon = computed((): 'person' | undefined => personHeaderAvatar.value ? undefined : 'person');

			const { entryDetailsRoute } = useEntryDetailsNavigation({
				organizationCode: toRef(props, 'organizationCode'),
				formMeta: toRef(props, 'formMeta'),
				currentView: toRef(props, 'currentView'),
				viewId: toRef(props, 'viewId'),
				internalEntryId: toRef(props, 'internalEntryId'),
				entryId: toRef(props, 'entryId')
			});

			const entryLinkAriaLabel = 'View entry in form\'s context';
			const entryLinkTooltip = 'Open entry details';

			const handleNavigate = () => {
				const targetRoute = entryDetailsRoute.value;
				if (!targetRoute)
					return;

				emit('navigate', targetRoute);
			};

			return {
				breadcrumbItems,
				avatarValue,
				avatarIcon,
				entryLinkAriaLabel,
				entryLinkTooltip,
				entryDetailsRoute,
				handleNavigate
			};
		}
	});
</script>

<style lang="scss">
	.entry-details-lightbox {

		&__header {
			padding-right: var(--spacing-modal-padding);
		}

		&__header-submission {

			.admin--mobile & {
				padding-top: var(--spacing-1);
			}
		}

		&__header-breadcrumb-item {

			+ .entry-details-lightbox__header-breadcrumb-item {

				&::before {
					padding: 0 4px;
					content: '/';
					color: var(--shade-7);
				}
			}
		}

		&__header-breadcrumb-icon {
			width: var(--spacing-icon-small) !important;
			height: var(--spacing-icon-small) !important;
		}

		&__header-summary,
		&__header-breadcrumb-label {
			@media print {
				text-wrap: wrap !important;
			}
		}

		&__header-actions {

			& .linked-person-avatar {
				// Ensure text is aligned with the status text on the row below
				margin-right: calc(-1 * (var(--spacing-1) + var(--spacing-2)));
			}
		}

		&__entry-link {

			.i-button__icon {
				width: $size__icon--large;
				height: $size__icon--large;
			}
		}
	}
</style>
